/**
 * SEO-friendly game URL slugs.
 *
 * Games are keyed by a UUID (e.g. a3cea28b-9f6d-4107-8e85-c6adc31daf81), which
 * is correct for database integrity but opaque and poor for SEO/readability in
 * a public URL. Instead of adding a slug column and backfilling the shared
 * production database, we derive a readable slug on the fly and append the
 * canonical UUID so the slug stays globally unique and the route can recover the
 * id without a lookup:
 *
 *   /game/lions-vs-tigers-a3cea28b-9f6d-4107-8e85-c6adc31daf81/boxscore
 *
 * Bare-UUID URLs continue to resolve (no existing link breaks); the box score
 * page upgrades them to the canonical slug URL in place.
 */

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

// Combining diacritical marks (U+0300..U+036F), left over after an NFKD
// normalize. Built with new RegExp so no literal combining marks live in source.
const DIACRITICS_RE = new RegExp('[\\u0300-\\u036f]', 'g');

/** Turn a team name into a URL-safe, lowercase, dash-separated slug fragment. */
export function slugifyTeamName(name: string | undefined | null): string {
  return (name || '')
    .toString()
    .normalize('NFKD') // split accented characters so the marks can be stripped
    .replace(DIACRITICS_RE, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-') // any run of non-alphanumerics becomes a single dash
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Build the human-readable game path segment: `away-vs-home-<uuid>`. Falls back
 * to the bare id when either team name is missing (so the URL is always valid).
 */
export function buildGameSlug(
  awayName: string | undefined | null,
  homeName: string | undefined | null,
  gameId: string,
): string {
  const away = slugifyTeamName(awayName);
  const home = slugifyTeamName(homeName);
  if (!away || !home) return gameId;
  return `${away}-vs-${home}-${gameId}`;
}

/**
 * Recover the canonical game id from a route segment that may be either a bare
 * id or a slug ending in the id. Returns the embedded UUID when present,
 * otherwise the segment unchanged (covers any legacy non-UUID id).
 */
export function extractGameId(param: string | undefined | null): string {
  if (!param) return '';
  const match = param.match(UUID_RE);
  return match ? match[0] : param;
}
